/**
 * Contrato do calendário de escala: dias, ausências e visitas.
 *
 * Papel: aqui ficam `STATUS_DIA_CALENDARIO` e `PAPEL_NO_DIA`, de onde nascem os
 * enums `status_dia_calendario` e `papel_no_dia` do Postgres, o cálculo de
 * feriados em `feriadosDoAno`, a apuração de conflitos em `conflitosDoDia` e os
 * schemas que o formulário e as Functions usam.
 *
 * Como usar: quem monta um dia envia **identificadores de colaborador**, nunca
 * apelidos. O apelido é como a escala se lê, e `colaboradorId` é como ela se
 * liga — corrigir a grafia de um apelido no RH não pode desfazer escala
 * nenhuma, e é o que aconteceria se o vínculo fosse pelo texto.
 *
 * `conflitosDoDia` devolve o identificador de quem está em conflito e o tipo do
 * problema, e não a frase pronta: o nome de quem trabalha na casa é dado
 * pessoal, e montar a frase é da tela, que já tem a pessoa carregada e está
 * atrás do login.
 */
import { z } from 'zod';

import { dataSchema, idSchema, textoObrigatorio, textoOpcional } from './comum';

/**
 * As situações que um dia inteiro pode ter, todas ao mesmo tempo.
 *
 * São multi-valor porque um dia é com frequência `familia` **e**
 * `todos_residencia` — a família está na casa e ninguém folga. Guardar isso
 * numa coluna só obrigaria a inventar um valor composto para cada combinação,
 * e a combinação seguinte exigiria mais um.
 */
export const STATUS_DIA_CALENDARIO = [
  'familia',
  'todos_residencia',
  'folga_total',
  'feriado',
] as const;

/** Em que situação uma pessoa está num dia da escala. */
export const PAPEL_NO_DIA = ['plantao', 'folga'] as const;

export const statusDiaSchema = z.enum(STATUS_DIA_CALENDARIO, {
  message: 'Situação de dia desconhecida.',
});

export const papelNoDiaSchema = z.enum(PAPEL_NO_DIA, {
  message: 'Papel desconhecido.',
});

export type StatusDiaCalendario = z.infer<typeof statusDiaSchema>;
export type PapelNoDia = z.infer<typeof papelNoDiaSchema>;

/**
 * Se o dia dispensa escala individual.
 *
 * `todos_residencia` e `folga_total` já dizem o que vale para todos, e num dia
 * assim a ausência de alguém nas listas não é esquecimento — é o normal. É essa
 * distinção que impede `conflitosDoDia` de acusar as oito pessoas de uma vez
 * todo domingo de folga geral.
 *
 * @param status - As situações do dia, como `novoDiaCalendarioSchema` as aceita.
 * @returns `true` quando o dia vale igual para a escala inteira.
 */
export function diaValeParaTodos(status: readonly StatusDiaCalendario[]): boolean {
  return status.includes('todos_residencia') || status.includes('folga_total');
}

/* ------------------------------------------------------------------ feriados */

/** Um instante em UTC, para que somar dias nunca esbarre em horário de verão. */
function emUtc(ano: number, mes: number, dia: number): Date {
  return new Date(Date.UTC(ano, mes - 1, dia));
}

/** O mesmo instante deslocado, em dias inteiros. */
function deslocar(data: Date, dias: number): Date {
  return new Date(data.getTime() + dias * 86_400_000);
}

/** A chave `MM-DD` de uma data, que é como `feriadosDoAno` indexa o ano. */
function mesEDia(data: Date): string {
  const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
  const dia = String(data.getUTCDate()).padStart(2, '0');
  return `${mes}-${dia}`;
}

/**
 * O domingo de Páscoa de um ano, pelo algoritmo de Meeus.
 *
 * É daqui que saem os cinco feriados móveis, todos contados em dias a partir
 * deste domingo. Tabelar as datas em vez de calculá-las obrigaria a acrescentar
 * uma linha por ano, e o ano esquecido apareceria como um Carnaval comum.
 *
 * @param ano - O ano com quatro dígitos.
 * @returns O domingo de Páscoa, em UTC.
 */
export function domingoDePascoa(ano: number): Date {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return emUtc(ano, mes, dia);
}

/** Os feriados de data fixa, nacionais e os de Porto Feliz. */
const FERIADOS_FIXOS: ReadonlyArray<readonly [string, string]> = [
  ['01-01', 'Confraternização Universal'],
  ['04-21', 'Tiradentes'],
  ['05-01', 'Dia do Trabalho'],
  ['07-09', 'Revolução Constitucionalista'],
  ['08-15', 'N. Sra. Mãe dos Homens'],
  ['09-07', 'Independência do Brasil'],
  ['10-12', 'N. Sra. Aparecida'],
  ['10-13', 'Aniversário de Porto Feliz'],
  ['11-02', 'Finados'],
  ['11-15', 'Proclamação da República'],
  ['11-20', 'Consciência Negra'],
  ['12-24', 'Véspera de Natal'],
  ['12-25', 'Natal'],
  ['12-31', 'Véspera de Ano Novo'],
];

/** Os móveis, em dias de distância do domingo de Páscoa. */
const FERIADOS_MOVEIS: ReadonlyArray<readonly [number, string]> = [
  [-48, 'Carnaval'],
  [-47, 'Carnaval'],
  [-46, 'Quarta-feira de Cinzas'],
  [-2, 'Sexta-feira Santa'],
  [60, 'Corpus Christi'],
];

/**
 * Os feriados de um ano, indexados por `MM-DD`.
 *
 * A lista mistura feriado nacional e municipal de Porto Feliz, que é onde a
 * casa fica — `Aniversário de Porto Feliz` não é feriado em lugar nenhum além
 * dali, e sem ele a escala mostraria um dia útil onde a cidade inteira para.
 *
 * @param ano - O ano com quatro dígitos.
 * @returns Mapa de `MM-DD` para o nome do feriado.
 */
export function feriadosDoAno(ano: number): Map<string, string> {
  const pascoa = domingoDePascoa(ano);
  const feriados = new Map<string, string>(FERIADOS_FIXOS);
  for (const [dias, nome] of FERIADOS_MOVEIS) {
    feriados.set(mesEDia(deslocar(pascoa, dias)), nome);
  }
  return feriados;
}

/**
 * O feriado de um dia, quando houver.
 *
 * Responde só pelo feriado de calendário. O status `feriado` de um dia é outra
 * coisa — é marcação de quem administra, para o dia que a casa trata como
 * feriado sem que nenhuma lei diga isso.
 *
 * @param dia - A data em `AAAA-MM-DD`.
 * @returns O nome do feriado, ou `null` num dia comum.
 */
export function feriadoDoDia(dia: string): string | null {
  const ano = Number(dia.slice(0, 4));
  return feriadosDoAno(ano).get(dia.slice(5)) ?? null;
}

/* ----------------------------------------------------------------- conflitos */

/** O que há de errado num dia da escala. */
export type TipoDeConflito = 'plantao-e-folga' | 'ferias-na-escala' | 'sem-situacao';

/** Uma pendência de um dia, já atribuída a uma pessoa. */
export interface Conflito {
  colaboradorId: string;
  tipo: TipoDeConflito;
  critico: boolean;
}

/** Uma pessoa escalada num dia, no mínimo que a apuração precisa. */
export interface PessoaEscalada {
  colaboradorId: string;
  papel: PapelNoDia;
}

/**
 * Apura o que está errado na escala de um dia.
 *
 * São três pendências, e as duas primeiras são críticas porque descrevem a
 * mesma pessoa em dois lugares incompatíveis; a terceira é lembrete de
 * preenchimento. A apuração nunca recusa o dia — um dia pela metade precisa
 * poder ser salvo, e o aviso é o que faz alguém voltar nele.
 *
 * `sem-situacao` só é apurada quando o dia não vale para todos: em
 * `folga_total` ninguém aparece nas listas, e acusar as oito pessoas de uma vez
 * treinaria quem usa a ignorar o aviso inteiro.
 *
 * @param entrada - O dia a conferir: `status` e `escalados` como o formulário
 *   os tem em mãos, `deFerias` com quem tem ausência cobrindo o dia, e
 *   `daCasa` com quem deveria ter situação definida.
 * @returns Uma pendência por pessoa e tipo; vazio quando o dia está coerente.
 */
export function conflitosDoDia(entrada: {
  status: readonly StatusDiaCalendario[];
  escalados: readonly PessoaEscalada[];
  deFerias: readonly string[];
  daCasa: readonly string[];
}): Conflito[] {
  const { status, escalados, deFerias, daCasa } = entrada;
  const conflitos: Conflito[] = [];

  const emPlantao = new Set(
    escalados.filter((p) => p.papel === 'plantao').map((p) => p.colaboradorId),
  );
  const emFolga = new Set(
    escalados.filter((p) => p.papel === 'folga').map((p) => p.colaboradorId),
  );

  for (const colaboradorId of emPlantao) {
    if (emFolga.has(colaboradorId)) {
      conflitos.push({ colaboradorId, tipo: 'plantao-e-folga', critico: true });
    }
  }

  for (const colaboradorId of deFerias) {
    if (emPlantao.has(colaboradorId) || emFolga.has(colaboradorId)) {
      conflitos.push({ colaboradorId, tipo: 'ferias-na-escala', critico: true });
    }
  }

  if (!diaValeParaTodos(status)) {
    const deFeriasNoDia = new Set(deFerias);
    for (const colaboradorId of daCasa) {
      const semSituacao =
        !emPlantao.has(colaboradorId) &&
        !emFolga.has(colaboradorId) &&
        !deFeriasNoDia.has(colaboradorId);
      if (semSituacao) {
        conflitos.push({ colaboradorId, tipo: 'sem-situacao', critico: false });
      }
    }
  }

  return conflitos;
}

/* ------------------------------------------------------------------ schemas */

/** Uma pessoa e o papel dela num dia, como o formulário a envia. */
export const pessoaEscaladaSchema = z.object({
  colaboradorId: idSchema,
  papel: papelNoDiaSchema,
});

/**
 * O que o formulário envia para criar ou alterar um dia da escala.
 *
 * As duas recusas existem porque nenhuma das duas é conserto automático: uma
 * pessoa repetida no dia é engano de digitação, e escolher por ela qual dos
 * dois papéis vale seria decidir uma escala de trabalho no lugar de quem a
 * monta. `unique(dia_id, colaborador_id)` diz a mesma coisa no banco, para quem
 * chamar a API por fora.
 */
export const novoDiaCalendarioSchema = z
  .object({
    residenciaId: idSchema,
    dia: dataSchema,
    status: z.array(statusDiaSchema).default([]),
    escalados: z.array(pessoaEscaladaSchema).default([]),
    extras: textoOpcional(500),
    observacao: textoOpcional(500),
    previsao: z.boolean().default(false),
    aDefinir: z.boolean().default(false),
  })
  .refine((valor) => new Set(valor.status).size === valor.status.length, {
    message: 'A mesma situação aparece duas vezes.',
    path: ['status'],
  })
  .refine(
    (valor) =>
      new Set(valor.escalados.map((p) => p.colaboradorId)).size === valor.escalados.length,
    { message: 'A mesma pessoa aparece duas vezes no dia.', path: ['escalados'] },
  );

export type NovoDiaCalendario = z.infer<typeof novoDiaCalendarioSchema>;

/** A pessoa de um dia, como a listagem a traz aninhada. */
export const pessoaDoDiaSchema = z.object({
  colaboradorId: idSchema,
  nomeCompleto: z.string(),
  apelido: z.string().nullable(),
  papel: papelNoDiaSchema,
});

/** A residência do dia, como a listagem a traz aninhada. */
export const residenciaDoCalendarioSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/** Um dia da escala como a API o devolve. */
export const diaCalendarioSchema = z.object({
  id: idSchema,
  residencia: residenciaDoCalendarioSchema,
  dia: dataSchema,
  status: z.array(statusDiaSchema),
  escalados: z.array(pessoaDoDiaSchema),
  extras: z.string().nullable(),
  observacao: z.string().nullable(),
  /** O dia está marcado como previsão, ainda não confirmado. */
  previsao: z.boolean(),
  /** O dia está marcado como a definir. */
  aDefinir: z.boolean(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type DiaCalendario = z.infer<typeof diaCalendarioSchema>;

/**
 * O que o formulário envia para criar ou alterar uma ausência.
 *
 * Um dia só de ausência se escreve com `inicio` e `fim` iguais, e é por isso
 * que a comparação aceita igualdade. Férias são intervalo fechado nas duas
 * pontas: quem volta no dia 14 tem `fim` no dia 13.
 */
export const novaAusenciaSchema = z
  .object({
    colaboradorId: idSchema,
    inicio: dataSchema,
    fim: dataSchema,
    observacao: textoOpcional(500),
  })
  .refine((valor) => valor.fim >= valor.inicio, {
    message: 'O fim não pode ser anterior ao início.',
    path: ['fim'],
  });

export type NovaAusencia = z.infer<typeof novaAusenciaSchema>;

/** O colaborador da ausência, como a listagem o traz aninhado. */
export const colaboradorDaAusenciaSchema = z.object({
  id: idSchema,
  nomeCompleto: z.string(),
  apelido: z.string().nullable(),
});

/** Uma ausência como a API a devolve. */
export const ausenciaSchema = z.object({
  id: idSchema,
  colaborador: colaboradorDaAusenciaSchema,
  inicio: dataSchema,
  fim: dataSchema,
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Ausencia = z.infer<typeof ausenciaSchema>;

/**
 * O que o formulário envia para criar ou alterar uma visita.
 *
 * `visitante` é texto, e não identificador: quem visita a casa nem sempre está
 * cadastrado em lugar nenhum do sistema, e exigir cadastro para anotar uma
 * visita de fim de semana faria a anotação não acontecer.
 */
export const novaVisitaSchema = z
  .object({
    residenciaId: idSchema,
    visitante: textoObrigatorio('Visitante', 120),
    inicio: dataSchema,
    fim: dataSchema,
    observacao: textoOpcional(500),
  })
  .refine((valor) => valor.fim >= valor.inicio, {
    message: 'O fim não pode ser anterior ao início.',
    path: ['fim'],
  });

export type NovaVisita = z.infer<typeof novaVisitaSchema>;

/** Uma visita como a API a devolve. */
export const visitaSchema = z.object({
  id: idSchema,
  residencia: residenciaDoCalendarioSchema,
  visitante: z.string(),
  inicio: dataSchema,
  fim: dataSchema,
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Visita = z.infer<typeof visitaSchema>;

/**
 * O recorte que as três listagens do calendário aceitam.
 *
 * A tela pede sempre um mês, e nunca a base inteira: o calendário ganha uma
 * linha por dia registrado e nunca perde nenhuma, e uma listagem sem recorte
 * cresce até estourar os 45 segundos de teto da plataforma — sem nunca dar erro
 * antes disso, porque o que a derruba é o tempo, não o tamanho.
 *
 * `de` e `ate` são inclusivos nas duas pontas, e o recorte pega tudo que
 * **encosta** no período: uma ausência que começa antes de `de` e termina
 * depois entra, porque cobre os dias do meio.
 */
export const recorteDoCalendarioSchema = z
  .object({
    residenciaId: idSchema,
    de: dataSchema,
    ate: dataSchema,
  })
  .refine((valor) => valor.ate >= valor.de, {
    message: 'O fim do período não pode ser anterior ao início.',
    path: ['ate'],
  });

export type RecorteDoCalendario = z.infer<typeof recorteDoCalendarioSchema>;

/**
 * Se um intervalo fechado cobre um dia.
 *
 * A comparação é de texto, e funciona porque a data trafega em ISO 8601, onde a
 * ordem alfabética é a ordem cronológica. Em formato brasileiro a mesma
 * comparação ordenaria por dia antes de mês e erraria sem avisar.
 *
 * @param intervalo - O que tem `inicio` e `fim`, como ausência e visita têm.
 * @param dia - A data em `AAAA-MM-DD`.
 * @returns `true` quando o dia está dentro, incluindo as duas pontas.
 */
export function cobreODia(intervalo: { inicio: string; fim: string }, dia: string): boolean {
  return intervalo.inicio <= dia && intervalo.fim >= dia;
}

/* ----------------------------------------------------------------- a equipe */

/**
 * A casa cujo cadastro do calendário se lê: a equipe, os prestadores e a
 * escala padrão são da casa inteira, e não de um período.
 */
export const casaDoCalendarioSchema = z.object({ residenciaId: idSchema });

/**
 * O que a coluna envia para pôr alguém na escala da casa.
 *
 * A pessoa entra pelo identificador do cadastro de pessoal, e não pelo nome
 * digitado: a escala se liga por chave, e quem não está no cadastro precisa ser
 * cadastrado lá antes de ganhar plantão ou folga.
 */
export const novoMembroDaEquipeSchema = z.object({
  residenciaId: idSchema,
  colaboradorId: idSchema,
});

export type NovoMembroDaEquipe = z.infer<typeof novoMembroDaEquipeSchema>;

/** Uma pessoa da escala da casa, na ordem em que entrou nela. */
export const membroDaEquipeSchema = z.object({
  id: idSchema,
  colaborador: colaboradorDaAusenciaSchema,
});

export type MembroDaEquipe = z.infer<typeof membroDaEquipeSchema>;

/* ------------------------------------------------------------ os prestadores */

/**
 * O que a coluna envia para cadastrar um prestador esporádico.
 *
 * O nome é o mesmo texto que a diária guarda em `visitante`: a lista existe
 * para a diária ser escolhida, e não digitada, e é isso que impede a mesma
 * pessoa de virar duas grafias.
 */
export const novoPrestadorSchema = z.object({
  residenciaId: idSchema,
  nome: textoObrigatorio('Nome', 120),
});

export type NovoPrestador = z.infer<typeof novoPrestadorSchema>;

/** Um prestador cadastrado, na ordem em que entrou na lista. */
export const prestadorSchema = z.object({
  id: idSchema,
  nome: z.string(),
});

export type Prestador = z.infer<typeof prestadorSchema>;

/* -------------------------------------------------------- a escala padrão */

/**
 * O dia da semana como o `Date.getDay` o conta: 0 é domingo, 6 é sábado.
 *
 * É a contagem do `getDay`, e não a da grade, porque a grade muda de primeiro
 * dia conforme a escolha de quem olha, e a escala padrão de uma segunda-feira
 * não pode mudar junto.
 */
export const diaDaSemanaSchema = z.number().int().min(0).max(6);

/**
 * A escala padrão de um dia da semana.
 *
 * `status`, quando existe, substitui as situações do dia ao aplicar o padrão;
 * sem ele, as situações marcadas continuam. `plantaoAutomatico` põe de plantão
 * toda a escala que não está em `folgas`; sem ele, ninguém fica de plantão.
 */
export const escalaPadraoDoDiaSchema = z.object({
  diaDaSemana: diaDaSemanaSchema,
  status: statusDiaSchema.nullable(),
  plantaoAutomatico: z.boolean(),
  /** Quem folga no padrão, na ordem em que a linha "Padrão: …" os escreve. */
  folgas: z.array(colaboradorDaAusenciaSchema),
});

export type EscalaPadraoDoDia = z.infer<typeof escalaPadraoDoDiaSchema>;

/* -------------------------------------------------------- a cópia da semana */

/**
 * O que a caixa "Copiar semana anterior" envia.
 *
 * `origem` e `destino` são o primeiro dia de cada semana. Os sete dias a partir
 * de `destino` passam a ser a cópia dos sete a partir de `origem`, e o dia de
 * origem sem registro apaga o de destino. `ausencias` e `visitas` são os
 * períodos marcados na caixa, gravados de novo deslocados pela distância entre
 * as duas datas; os originais ficam onde estão.
 */
export const copiaDaSemanaSchema = z
  .object({
    residenciaId: idSchema,
    origem: dataSchema,
    destino: dataSchema,
    ausencias: z.array(idSchema).default([]),
    visitas: z.array(idSchema).default([]),
  })
  .refine((valor) => valor.destino !== valor.origem, {
    message: 'A semana de destino precisa ser outra.',
    path: ['destino'],
  });

export type CopiaDaSemana = z.infer<typeof copiaDaSemanaSchema>;

/**
 * Uma data deslocada em dias inteiros.
 *
 * A conta é feita em UTC, onde todo dia tem 24 horas: no fuso local, a virada
 * do horário de verão faria um dos dias ter 23, e a data voltaria um dia antes.
 *
 * @param dia - A data em `AAAA-MM-DD`.
 * @param dias - Quantos dias somar; negativo volta.
 * @returns A data deslocada, em `AAAA-MM-DD`.
 */
export function deslocarData(dia: string, dias: number): string {
  const [ano, mes, numero] = dia.split('-').map(Number);
  const base = Date.UTC(ano ?? 1970, (mes ?? 1) - 1, numero ?? 1);
  return new Date(base + dias * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Quantos dias separam duas datas.
 *
 * @param de - A data de partida, em `AAAA-MM-DD`.
 * @param ate - A data de chegada, em `AAAA-MM-DD`.
 * @returns A diferença em dias, negativa quando `ate` vem antes.
 */
export function diasEntre(de: string, ate: string): number {
  const emUtcDoDia = (dia: string) => {
    const [ano, mes, numero] = dia.split('-').map(Number);
    return Date.UTC(ano ?? 1970, (mes ?? 1) - 1, numero ?? 1);
  };
  return Math.round((emUtcDoDia(ate) - emUtcDoDia(de)) / 86_400_000);
}
